import { useEffect, useState, useCallback } from "react";
import Draggable from "react-draggable";
import { getAvailableFonts } from "@remotion/google-fonts";

function ElementSettingsToolbar({
  carousel,
  template,
  currentSlideElement,
  duplicateElement,
  deleteElement,
  updateElementStyle,
  getPropertyByStyleKey,
  customFonts,
  onClose,
}) {
  const [fonts, setFonts] = useState(
    getAvailableFonts().map((font) => ({
      ...font,
      isCustom: false,
    }))
  );
  let slide = carousel.slides[currentSlideElement.slideIndex];
  let elementStyle = slide.elements[currentSlideElement.elementIndex]?.style;
  const [style, setStyle] = useState(() => ({
    ...elementStyle,
    fontFamily: elementStyle.fontFamily || { value: "" },
  }));
  const [customFontSize, setCustomFontSize] = useState("");
  const [showFontSize, setShowFontSize] = useState(false);
  const [showLineHeight, setShowLineHeight] = useState(false);


  useEffect(() => {
    const userCustomFonts = customFonts.map((font) => ({
      ...font,
      isCustom: true,
    }));
    setFonts((prevFonts) => [...prevFonts, ...userCustomFonts]);
  }, [customFonts]);

  const updateStyleFromCommonSettings = useCallback(() => {
    const updatedStyle = { ...elementStyle };

    Object.entries(elementStyle).forEach(([styleKey, styleValue]) => {
      if (styleValue.key) {
        const commonValue = carousel.common_settings?.elements?.find(
          (element) => element.key === styleValue.key
        )?.value;
        if (!updatedStyle[styleKey]?.value) {
          updatedStyle[styleKey] = { ...styleValue, value: commonValue };
        }
      }
    });

    setStyle(prevStyle => ({
      ...prevStyle,
      ...updatedStyle,
      fontFamily: updatedStyle.fontFamily || prevStyle.fontFamily || { value: '' }
    }));
  }, [carousel, elementStyle]);

  useEffect(() => {
    updateStyleFromCommonSettings();
  }, [updateStyleFromCommonSettings, currentSlideElement]);

  const fontSizeArr = [
    "12px",
    "14px",
    "16px",
    "20px",
    "24px",
    "30px",
    "36px",
    "48px",
  ];

  const lineHeightArr = [
    { label: "1", value: "1" },
    { label: "1.15", value: "1.15" },
    { label: "1.25", value: "1.25" },
    { label: "1.5", value: "1.5" },
    { label: "1.75", value: "1.75" },
    { label: "2", value: "2" },
  ];

  const updateStyles = (styleKey, value) => {
    if (!style[styleKey]) {
      style[styleKey] = {
        value: value,
      };
    } else {
      style[styleKey].value = value;
    }
    setStyle(style);

    updateElementStyle(styleKey, value);
  };

  const onChangeFontSize = (e) => {
    e.preventDefault();
    const value = e.target.value;
    setCustomFontSize(`${value}`);
  };

  const onClickUpdateStyles = (e) => {
    if (!e.target?.dataset?.fontSize) return;

    const value = e.target?.dataset?.fontSize;

    updateStyles("fontSize", value);
    setShowFontSize(false);
  };

  const onClickSetCustomFontSize = () => {
    updateStyles("fontSize", `${customFontSize}px`);
    setShowFontSize(false);
  };

  return (
    <>
      {currentSlideElement && style && (
        <Draggable>
          <div className="absolute z-40 right-8 top-32">
            {/* START TOOLBAR */}
            <div className="p-2 bg-white shadow-xl rounded-xl ring-1 ring-inset ring-gray-900/20">
              <div className="flex items-center gap-3">
                {(currentSlideElement.elementType === "text" ||
                  currentSlideElement.elementType === "multiline") && (
                  <>
                    <div className="flex items-center gap-2 shrink-0">
                      <div>
                        <label htmlFor="" className="sr-only">
                          Font Family
                        </label>
                        <select
                          onChange={async (e) => {
                            const font = fonts[e.target.selectedIndex];
                            if(font.isCustom){
                              updateStyles("fontFamily", font.fontFamily);
                            } else {
                            font.load().then((l) => {
                              l.loadFont();
                              updateStyles("fontFamily", font.fontFamily);
                            });
                          }
                          }}
                          value={style.fontFamily?.value}
                          name=""
                          id=""
                          className="block w-full rounded-lg border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-500 sm:text-sm sm:leading-6 hover:bg-gray-100"
                        >
                          {fonts.map((f) => {
                            return (
                              <option key={f.importName} value={f.fontFamily}>
                                {f.fontFamily}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="" className="sr-only">
                          Font Size
                        </label>
                        <div className="relative">
                          <div
                            onClick={() => setShowFontSize(!showFontSize)}
                            className="block w-full rounded-lg border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-500 sm:text-sm sm:leading-6 hover:bg-gray-100"
                          >
                            {style.fontSize?.value}
                          </div>

                          {showFontSize && (
                            <div className="absolute">
                              <div className="top-full bg-white left-0 border rounded-lg py-1 px-1 max-h-96 overflow-y-auto w-20">
                                <div>
                                  <input
                                    value={customFontSize}
                                    onChange={onChangeFontSize}
                                    className="w-full p-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm text-center"
                                    placeholder="e.g.,13px"
                                  />
                                  {customFontSize &&
                                    !fontSizeArr.includes(
                                      `${customFontSize}px`
                                    ) && (
                                      <button
                                        onClick={onClickSetCustomFontSize}
                                        className="flex items-center gap-1 text-sm h-6 hover:bg-gray-50 mt-1 mx-auto"
                                      >
                                        <svg
                                          width="16"
                                          height="17"
                                          viewBox="0 0 16 17"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8.00002 2.59131C8.2122 2.59131 8.41568 2.67559 8.56571 2.82562C8.71574 2.97565 8.80002 3.17914 8.80002 3.39131V7.39131H12.8C13.0122 7.39131 13.2157 7.47559 13.3657 7.62562C13.5157 7.77565 13.6 7.97914 13.6 8.19131C13.6 8.40348 13.5157 8.60697 13.3657 8.75699C13.2157 8.90702 13.0122 8.99131 12.8 8.99131H8.80002V12.9913C8.80002 13.2035 8.71574 13.407 8.56571 13.557C8.41568 13.707 8.2122 13.7913 8.00002 13.7913C7.78785 13.7913 7.58437 13.707 7.43434 13.557C7.28431 13.407 7.20002 13.2035 7.20002 12.9913V8.99131H3.20002C2.98785 8.99131 2.78437 8.90702 2.63434 8.75699C2.48431 8.60697 2.40002 8.40348 2.40002 8.19131C2.40002 7.97914 2.48431 7.77565 2.63434 7.62562C2.78437 7.47559 2.98785 7.39131 3.20002 7.39131H7.20002V3.39131C7.20002 3.17914 7.28431 2.97565 7.43434 2.82562C7.58437 2.67559 7.78785 2.59131 8.00002 2.59131Z"
                                            fill="#0BA5EC"
                                          />
                                        </svg>
                                        <span className="text-xs font-medium text-gray-900 py-0.5 px-1 rounded-md bg-gray-100">
                                          {customFontSize}px
                                        </span>
                                      </button>
                                    )}
                                  <div onClick={onClickUpdateStyles}>
                                    {fontSizeArr.map((fontSize) => (
                                      <div
                                        key={fontSize}
                                        data-font-size={fontSize}
                                        className="flex items-center justify-center gap-2 rounded-lg hover:bg-blue-100 cursor-pointer overflow-hidden p-0.5 px-3 h-8 bg-white w-full text-sm text-gray-900"
                                      >
                                        {fontSize}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label htmlFor="" className="sr-only">
                          Text Color
                        </label>
                        <div className="p-2 transition-all duration-150 rounded-lg cursor-pointer ring-1 ring-inset ring-gray-300 hover:bg-gray-200">
                          <div
                            className="w-5 h-5 rounded-full"
                            style={{
                              backgroundColor:
                                style.color?.value ||
                                template.getPropertyByStyleKey(
                                  style.color?.key,
                                  null
                                ),
                            }}
                          >
                            <input
                              type="color"
                              name=""
                              id=""
                              placeholder=""
                              value={style.color?.value}
                              onChange={(e) => {
                                updateStyles("color", e.target.value);
                              }}
                              className="inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="" className="sr-only">
                          Line Spacing
                        </label>
                        <div className="relative">
                          <button
                            onClick={() => setShowLineHeight(!showLineHeight)}
                            className="flex items-center gap-1 rounded-lg border-0 py-1.5 px-2 text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-100 sm:text-sm sm:leading-6"
                            title="Line Spacing"
                          >
                            <svg
                              aria-hidden="true"
                              className="w-5 h-5"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 10H7" />
                              <path d="M21 6H7" />
                              <path d="M21 14H7" />
                              <path d="M21 18H7" />
                              <path d="M3 5v14" />
                              <path d="m6 7-3-2-3 2" />
                              <path d="m6 17-3 2-3-2" />
                            </svg>
                          </button>

                          {showLineHeight && (
                            <div className="absolute top-full mt-1 z-50">
                              <div className="bg-white border rounded-lg py-1 px-1 max-h-96 overflow-y-auto w-20">
                                {lineHeightArr.map((lh) => (
                                  <div
                                    key={lh.value}
                                    onClick={() => {
                                      updateStyles("lineHeight", lh.value);
                                      setShowLineHeight(false);
                                    }}
                                    className={
                                      "flex items-center justify-center gap-2 rounded-lg hover:bg-blue-100 cursor-pointer overflow-hidden p-0.5 px-3 h-8 w-full text-sm text-gray-900" +
                                      (style.lineHeight?.value === lh.value
                                        ? " bg-blue-50 font-medium"
                                        : " bg-white")
                                    }
                                  >
                                    {lh.label}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w-px h-6 bg-gray-300 shrink-0" />
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          if (style.fontWeight?.value === "bold") {
                            updateStyles("fontWeight", "normal");
                          } else {
                            updateStyles("fontWeight", "bold");
                          }
                        }}
                        type="button"
                        className={
                          "p-2 transition-all duration-150 rounded-lg hover:text-gray-700 hover:bg-gray-200" +
                          (style.fontWeight?.value === "bold"
                            ? " text-gray-700 bg-gray-200"
                            : " text-gray-500 bg-transparent ")
                        }
                      >
                        <span className="sr-only">Bold</span>
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 256 256"
                        >
                          <path d="M177.08,114.46A48,48,0,0,0,140,36H72A12,12,0,0,0,60,48V200a12,12,0,0,0,12,12h80a52,52,0,0,0,25.08-97.54ZM84,60h56a24,24,0,0,1,0,48H84Zm68,128H84V132h68a28,28,0,0,1,0,56Z"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          if (style.fontStyle?.value === "italic") {
                            updateStyles("fontStyle", "normal");
                          } else {
                            updateStyles("fontStyle", "italic");
                          }
                        }}
                        type="button"
                        className={
                          "p-2 transition-all duration-150 rounded-lg hover:text-gray-700 hover:bg-gray-200" +
                          (style.fontStyle?.value === "italic"
                            ? " text-gray-700 bg-gray-200"
                            : " text-gray-500 bg-transparent ")
                        }
                      >
                        <span className="sr-only">Italic</span>
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 256 256"
                        >
                          <path d="M204,56a12,12,0,0,1-12,12H160.65l-40,120H144a12,12,0,0,1,0,24H64a12,12,0,0,1,0-24H95.35l40-120H112a12,12,0,0,1,0-24h80A12,12,0,0,1,204,56Z"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          if (style.textAlign?.value === "left") {
                            updateStyles("textAlign", "left");
                          } else {
                            updateStyles("textAlign", "left");
                          }
                        }}
                        type="button"
                        className={
                          "p-2 transition-all duration-150 rounded-lg hover:text-gray-700 hover:bg-gray-200" +
                          (style.textAlign?.value === "left"
                            ? " text-gray-700 bg-gray-200"
                            : " text-gray-500 bg-transparent ")
                        }
                      >
                        <span className="sr-only">Left-aligned</span>
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 256 256"
                        >
                          <path d="M28,64A12,12,0,0,1,40,52H216a12,12,0,0,1,0,24H40A12,12,0,0,1,28,64Zm12,52H168a12,12,0,0,0,0-24H40a12,12,0,0,0,0,24Zm176,16H40a12,12,0,0,0,0,24H216a12,12,0,0,0,0-24Zm-48,40H40a12,12,0,0,0,0,24H168a12,12,0,0,0,0-24Z"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          if (style.textAlign?.value === "center") {
                            updateStyles("textAlign", "left");
                          } else {
                            updateStyles("textAlign", "center");
                          }
                        }}
                        type="button"
                        className={
                          "p-2 transition-all duration-150 rounded-lg hover:text-gray-700 hover:bg-gray-200" +
                          (style.textAlign?.value === "center"
                            ? " text-gray-700 bg-gray-200"
                            : " text-gray-500 bg-transparent ")
                        }
                      >
                        <span className="sr-only">Center-aligned</span>
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 256 256"
                        >
                          <path d="M28,64A12,12,0,0,1,40,52H216a12,12,0,0,1,0,24H40A12,12,0,0,1,28,64ZM64,92a12,12,0,0,0,0,24H192a12,12,0,0,0,0-24Zm152,40H40a12,12,0,0,0,0,24H216a12,12,0,0,0,0-24Zm-24,40H64a12,12,0,0,0,0,24H192a12,12,0,0,0,0-24Z"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          if (style.textAlign?.value === "right") {
                            updateStyles("textAlign", "left");
                          } else {
                            updateStyles("textAlign", "right");
                          }
                        }}
                        type="button"
                        className={
                          "p-2 transition-all duration-150 rounded-lg hover:text-gray-700 hover:bg-gray-200" +
                          (style.textAlign?.value === "right"
                            ? " text-gray-700 bg-gray-200"
                            : " text-gray-500 bg-transparent ")
                        }
                      >
                        <span className="sr-only">Right-aligned</span>
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 256 256"
                        >
                          <path d="M28,64A12,12,0,0,1,40,52H216a12,12,0,0,1,0,24H40A12,12,0,0,1,28,64ZM216,92H88a12,12,0,0,0,0,24H216a12,12,0,0,0,0-24Zm0,40H40a12,12,0,0,0,0,24H216a12,12,0,0,0,0-24Zm0,40H88a12,12,0,0,0,0,24H216a12,12,0,0,0,0-24Z"></path>
                        </svg>
                      </button>
                    </div>
                  </>
                )}
                {carousel.template_id === "4" && (
                  <>
                    <div className="w-px h-6 bg-gray-300 shrink-0" />
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          duplicateElement();
                        }}
                        type="button"
                        className="p-2 text-gray-500 transition-all duration-150 bg-transparent rounded-lg hover:text-gray-700 hover:bg-gray-200"
                      >
                        <span className="sr-only">Duplicate</span>
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                          <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => deleteElement()}
                        type="button"
                        className="p-2 text-gray-500 transition-all duration-150 bg-transparent rounded-lg hover:text-gray-700 hover:bg-gray-200"
                      >
                        <span className="sr-only">Delete</span>
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  </>
                )}
                <div className="w-px h-6 bg-gray-300 shrink-0" />
                <button
                  onClick={onClose}
                  type="button"
                  className="p-2 text-gray-500 transition-all duration-150 bg-transparent rounded-lg hover:text-gray-700 hover:bg-gray-200"
                >
                  <span className="sr-only">Close Toolbar</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* END TOOLBAR */}
          </div>
        </Draggable>
      )}
    </>
  );
}

export default ElementSettingsToolbar;
